import React, { useState } from "react";
import { Flex, Text, Button, Divider, Box, Loader } from "@mantine/core";
import axios from "axios";
import toast from "react-hot-toast";
import styled from "styled-components";

import { VinForm } from "@components/RideChallenge/VinSetup";
import VehicleCard from "./VehicleCard";
import OdometerValueMismatch from "./OdometerValueMismatch";
import OdometerForm from "./OdometerForm";
import NhtsaResults from "./NhtsaResults";
import OdometerMismatchModal from "./OdometerMismatchModal";
import {
  nhtsaMakeIsValid,
  nhtsaTypeIsValid,
  getDataFromNhtsaResults,
} from "./helpers";

const StyledOdometerForm = styled.div`
  background: var(--mantine-color-dark-8);
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AddVin = ({
  onSuccess,
  originalData,
  additionalReqData,
  isOverride,
  adminEdit,
  onAdminSubmit,
  adminOverride,
  adminVin,
  isVehicleDataEffort = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [newVinInfo, setNewVinInfo] = useState(null);
  const [value, setValue] = useState("");
  const [override, setOverride] = useState(false);
  const [showReading, setShowReading] = useState(false);
  const [vinNeedsModel, setVinNeedsModel] = useState(false);
  const [odometerMismatchModalOpen, setOdometerMismatchModalOpen] =
    useState(false);

  function resolveOdometerMismatch(val) {
    setValue(val);
    setOdometerMismatchModalOpen(false);
    onSubmit(val);
  }

  function onResetClick() {
    setResults(null);
    setNewVinInfo(null);
  }

  function onSubmit(odometerValueOverride = null) {
    const odometerValue =
      odometerValueOverride !== null ? odometerValueOverride : value;

    if (
      !adminEdit &&
      originalData.odometer_value !== parseInt(odometerValue) &&
      !odometerMismatchModalOpen
    ) {
      setOdometerMismatchModalOpen(true);
      return;
    }

    const nhtsaData = getDataFromNhtsaResults(
      results.nhtsa_results?.response
    ).reduce((acc, cur) => {
      acc[cur.server_key] = cur.value;
      return acc;
    }, {});

    const req = {
      ...additionalReqData,
      ...nhtsaData,
      vin: newVinInfo.vin,
      checksum_results: results.vin_checksum_results,
      nhtsa_results: results.nhtsa_results,
      odometer_value: odometerValue,
    };

    if (override) req.override = true;
    if (!req.make) req.make = "HARLEY-DAVIDSON";
    if (!req.model) req.model = newVinInfo.model;
    if (!req.year) req.year = newVinInfo.year;

    if (adminEdit) {
      const adminReq = {
        ...req,
        nhtsa_results_success: results?.nhtsa_results?.success || false,
      };

      if (!adminOverride) {
        adminReq.is_passenger = newVinInfo.is_passenger;
        adminReq.is_rental = newVinInfo.is_rental;
        adminReq.use_kilometers = newVinInfo.use_kilometers;
      }

      return onAdminSubmit(adminReq);
    }

    setLoading(true);

    axios
      .post(`/ride-challenge/verify-reading/`, req)
      .then(() => {
        toast.success("Verified!");
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function checkVin(formData) {
    const req = {
      ...formData,
    };

    setNewVinInfo(formData);

    setLoading(true);

    axios
      .post(`/user-vehicles/check-vin/`, req)
      .then(({ data }) => {
        setLoading(false);
        setResults(data.response[0]);
      })
      .catch((err) => {
        setLoading(false);
      });
  }

  const nhtsaSuccess = results?.nhtsa_results?.success;
  const isLegacyVin = newVinInfo && parseInt(newVinInfo.year) < 1981;
  const originalIsLegacyVin =
    originalData && parseInt(originalData.year) < 1981;
  const newVinVerified = isLegacyVin ? true : nhtsaSuccess;

  if (showReading) {
    return (
      <div>
        {newVinInfo && (
          <div>
            <Flex align="center" gap="xs">
              <Text size="xl" fw={600} c="white">
                VIN: {newVinInfo.vin?.toUpperCase()}
              </Text>
            </Flex>
            <Text c="dimmed" size="xs">
              manually entered data
            </Text>
            {/* {isOverride && ( */}
            {true && (
              <Box mb="lg">
                <VehicleCard info={newVinInfo} />
              </Box>
            )}
          </div>
        )}
        {isLegacyVin ? (
          <>
            <Text c="dimmed">
              NHTSA Results are not available for vehicles made before 1981.
            </Text>
            {/* <VehicleCard info={newVinInfo} /> */}
          </>
        ) : (
          <>
            {results.nhtsa_results && (
              <NhtsaResults results={results.nhtsa_results} />
            )}
          </>
        )}
        <Box mt="lg">
          <StyledOdometerForm>
            <OdometerForm value={value} onChange={(e) => setValue(e)} />
            {originalData.odometer_value !== undefined && (
              <OdometerValueMismatch
                originalValue={originalData.odometer_value}
                newValue={`${value}`}
              />
            )}
            <Flex justify="center">
              <Button
                disabled={!value}
                mt="lg"
                onClick={() => onSubmit()}
                loading={loading}
              >
                Submit
              </Button>
            </Flex>
            {odometerMismatchModalOpen && (
              <OdometerMismatchModal
                userValue={originalData.odometer_value}
                dealerValue={value}
                onClose={() => setOdometerMismatchModalOpen(false)}
                onSubmit={(val) => resolveOdometerMismatch(val)}
              />
            )}
            {loading && (
              <Text mt="lg" size="xs" fw={600} c="dimmed" align="center">
                Thank you for your patience. This may take up to 15 seconds.
              </Text>
            )}
          </StyledOdometerForm>
        </Box>
      </div>
    );
  }

  if (results) {
    const newVehicleMakeValid = isLegacyVin
      ? true
      : nhtsaMakeIsValid(
          results.nhtsa_results?.response,
          isVehicleDataEffort ? false : true
        );
    const newVehicleTypeValid = isLegacyVin
      ? true
      : nhtsaTypeIsValid(results.nhtsa_results?.response, ["1"]);
    const newVinHasModel = newVinInfo?.model;
    const needsModel =
      !newVinHasModel &&
      !results.nhtsa_results?.response?.find((f) => f.Variable === "Model")
        ? true
        : false;

    return (
      <div>
        {originalData && (
          <Box mt="lg">
            <Text fw={600} c="white">
              Original Data
            </Text>
            <Text size="xl" fw={600} c="white">
              VIN: {originalData.vin?.toUpperCase()}
            </Text>
            {originalIsLegacyVin ? (
              <>
                <Text c="dimmed">
                  NHTSA Results are not available for vehicles made before 1981.
                </Text>
                <VehicleCard info={originalData} />
              </>
            ) : (
              <>
                <Box mb="lg">
                  <VehicleCard info={originalData} />
                </Box>
                {results.nhtsa_results && (
                  <NhtsaResults results={originalData?.nhtsa_results} />
                )}
              </>
            )}
          </Box>
        )}
        {!adminEdit && <Divider mt="lg" mb="lg" label="vs." />}
        {newVinInfo && (
          <div>
            {newVehicleMakeValid && newVehicleTypeValid && (
              <Text fw={600} c="white">
                Proposed Changes
              </Text>
            )}
            <Flex
              align={{
                base: "flex-start",
                xs: "center",
              }}
              gap="xs"
              direction={{
                base: "column",
                xs: "row",
              }}
            >
              <Text size="xl" fw={600} c="white">
                VIN: {newVinInfo.vin?.toUpperCase()}
              </Text>
              {newVehicleMakeValid && newVehicleTypeValid && (
                <Button
                  onClick={() => {
                    onResetClick();
                  }}
                  color="dark.3"
                  size="compact-xs"
                  // variant="light"
                  radius="xl"
                  variant="subtle"
                >
                  edit VIN
                </Button>
              )}
            </Flex>
            {newVehicleMakeValid && newVehicleTypeValid && (
              <Box mb="lg">
                <VehicleCard info={newVinInfo} />
              </Box>
            )}
            {/* {isOverride && !isLegacyVin && (
              <Box mb="lg">
                <VehicleCard info={newVinInfo} />
              </Box>
            )} */}
          </div>
        )}
        {isLegacyVin ? (
          <>
            <Text c="dimmed">
              NHTSA Results are not available for vehicles made before 1981.
            </Text>
            {/* <VehicleCard info={newVinInfo} /> */}
          </>
        ) : (
          <>
            {results.nhtsa_results && (
              <NhtsaResults results={results.nhtsa_results} />
            )}
          </>
        )}
        {newVehicleMakeValid && newVehicleTypeValid && needsModel && (
          <Button
            mt="lg"
            size="lg"
            fullWidth
            onClick={() => {
              setOverride(true);
              setResults(null);
              setVinNeedsModel(true);
            }}
          >
            Proceed anyway
          </Button>
        )}
        {newVehicleMakeValid && newVehicleTypeValid && !needsModel && (
          <>
            {adminEdit ? (
              <>
                <Button
                  mt="lg"
                  size="lg"
                  color="green"
                  fullWidth
                  onClick={() => onSubmit()}
                >
                  Confirm VIN Change
                </Button>
              </>
            ) : (
              <Button
                mt="lg"
                size="lg"
                fullWidth
                onClick={() => {
                  if (newVinVerified) {
                    setOverride(false);
                  } else {
                    setOverride(true);
                  }
                  setShowReading(true);
                }}
              >
                {newVinVerified ? "Confirm Changes" : "Proceed anyway"}
              </Button>
            )}
          </>
        )}
        {!newVehicleMakeValid ||
          (!newVehicleTypeValid && (
            <>
              {!newVehicleMakeValid && (
                <Text mt="lg" c="red" fw={600} align="center">
                  {makeErrorMessage}
                </Text>
              )}
              {!newVehicleTypeValid && (
                <Text mt="lg" c="red" fw={600} align="center">
                  {vehicleTypeErrorMessage}
                </Text>
              )}
              {adminEdit && (
                <Flex justify="center" mt="lg">
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      setResults(null);
                    }}
                  >
                    Back
                  </Button>
                </Flex>
              )}
            </>
          ))}

        {/* <Box mt="lg">
          <StyledOdometerForm>
            <OdometerForm value={value} onChange={(e) => setValue(e)} />
          </StyledOdometerForm>
        </Box>
        <Button
          disabled={value.length !== 6}
          mt="lg"
          fullWidth
          onClick={onSubmit}
          loading={loading}
        >
          Submit
        </Button> */}
      </div>
    );
  }

  if (loading) {
    return (
      <Box
        mih={200}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Loader />
        <Text c="white" size="sm" fw={600} mt="md">
          Thank you for your patience. This may take up to 15 seconds.
        </Text>
      </Box>
    );
  }

  return (
    <div>
      <VinForm
        isOverride={isOverride || override}
        originalData={
          (isOverride || override) && newVinInfo
            ? {
                ...originalData,
                vin: newVinInfo.vin,
                make: newVinInfo.make,
                model: newVinInfo.model,
                year: newVinInfo.year,
              }
            : originalData
        }
        onSuccess={(vinData) => {
          checkVin(vinData);
        }}
        adminEdit={adminEdit}
        adminOverride={adminOverride}
        adminVin={adminVin}
        isVehicleDataEffort={isVehicleDataEffort}
      />
    </div>
  );
};

const makeErrorMessage =
  "This vehicle is not a Harley-Davidson. Please enter another VIN.";
const vehicleTypeErrorMessage = "Type is not valid. Please enter another VIN.";

export default AddVin;
